
import { useState, useEffect } from "react"
import Navbar from "../components/common/Navbar"
import { Link } from "react-router-dom"


export default function LandingPage() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0)
  const [stats, setStats] = useState({ jobs: 0, companies: 0, candidates: 0, placements: 0 })
  // Typing animation states
  const [locationText, setLocationText] = useState("")
  const [jobText, setJobText] = useState("")
  const [locationIndex, setLocationIndex] = useState(0)
  const [jobIndex, setJobIndex] = useState(0)
  const [isLocationDeleting, setIsLocationDeleting] = useState(false)
  const [isJobDeleting, setIsJobDeleting] = useState(false)

  const locations = ["Mumbai", "Delhi", "Chennai"]
  const jobTitles = ["Software Engineer", "Data Scientist", "Product Manager", "UI/UX Designer"]

  // Typing animation effect for location
  useEffect(() => {
    const currentLocation = locations[locationIndex]
    const timeout = setTimeout(() => {
      if (!isLocationDeleting) {
        if (locationText.length < currentLocation.length) {
          setLocationText(currentLocation.substring(0, locationText.length + 1))
        } else {
          setTimeout(() => setIsLocationDeleting(true), 1500)
        }
      } else {
        if (locationText.length > 0) {
          setLocationText(locationText.substring(0, locationText.length - 1))
        } else {
          setIsLocationDeleting(false)
          setLocationIndex((prev) => (prev + 1) % locations.length)
        }
      }
    }, isLocationDeleting ? 50 : 100)

    return () => clearTimeout(timeout)
  }, [locationText, locationIndex, isLocationDeleting, locations])

  // Typing animation effect for job
  useEffect(() => {
    const currentJob = jobTitles[jobIndex]
    const timeout = setTimeout(() => {
      if (!isJobDeleting) {
        if (jobText.length < currentJob.length) {
          setJobText(currentJob.substring(0, jobText.length + 1))
        } else {
          setTimeout(() => setIsJobDeleting(true), 2000)
        }
      } else {
        if (jobText.length > 0) {
          setJobText(jobText.substring(0, jobText.length - 1))
        } else {
          setIsJobDeleting(false)
          setJobIndex((prev) => (prev + 1) % jobTitles.length)
        }
      }
    }, isJobDeleting ? 75 : 150)

    return () => clearTimeout(timeout)
  }, [jobText, jobIndex, isJobDeleting, jobTitles])

  // Animate stats on load
  useEffect(() => {
    const animateStats = () => {
      const targets = { jobs: 15420, companies: 2840, candidates: 89650, placements: 12380 }
      const duration = 2000
      const steps = 60
      const stepTime = duration / steps

      let step = 0
      const timer = setInterval(() => {
        step++
        const progress = step / steps
        setStats({
          jobs: Math.floor(targets.jobs * progress),
          companies: Math.floor(targets.companies * progress),
          candidates: Math.floor(targets.candidates * progress),
          placements: Math.floor(targets.placements * progress),
        })

        if (step >= steps) {
          clearInterval(timer)
          setStats(targets)
        }
      }, stepTime)
    }

    animateStats()
  }, [])

  // Auto-rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)
    }, 5000)
    return () => clearInterval(timer)
  })

  const jobCategories = [
    {
      domain: "Engineering",
      icon: "💻",
      categories: ["Software Development", "Data Science", "Cybersecurity", "DevOps", "AI/ML"],
      jobs: 4520,
      color: "from-blue-500 to-indigo-600",
    },
    {
      domain: "Health & Medicine",
      icon: "🏥",
      categories: ["Nursing", "Medical", "Pharmacy", "Healthcare Admin", "Research"],
      jobs: 2840,
      color: "from-green-500 to-emerald-600",
    },
    {
      domain: "Finance",
      icon: "💰",
      categories: ["Banking", "Investment", "Accounting", "Insurance", "Fintech"],
      jobs: 1920,
      color: "from-yellow-500 to-orange-600",
    },
    {
      domain: "Marketing",
      icon: "📈",
      categories: ["Digital Marketing", "Content", "SEO/SEM", "Social Media", "Brand Management"],
      jobs: 1680,
      color: "from-pink-500 to-rose-600",
    },
    {
      domain: "UI",
      icon: "⚙️",
      categories: ["Designing", "UX Expert", "UI Designer"],
      jobs: 2150,
      color: "from-gray-500 to-slate-600",
    },
    {
      domain: "Education",
      icon: "🎓",
      categories: ["Teaching", "Administration", "Curriculum", "Training", "E-learning"],
      jobs: 980,
      color: "from-purple-500 to-violet-600",
    },
  ]

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Software Engineer at TechCorp",
      image: "SJ",
      content:
        "Jobs247 helped me find my dream job in just 2 weeks! The platform is intuitive and the job matches were perfect.",
      rating: 5,
    },
    {
      name: "Michael Chen",
      role: "Marketing Director at StartupXYZ",
      image: "MC",
      content:
        "As a recruiter, I love how easy it is to post jobs and find qualified candidates. The filtering system is excellent.",
      rating: 5,
    },
    {
      name: "Emily Rodriguez",
      role: "Data Scientist at DataFlow",
      image: "ER",
      content:
        "The career resources and interview tips were invaluable. I felt prepared and confident throughout my job search.",
      rating: 5,
    },
  ]

  const features = [
    {
      icon: "🔍",
      title: "Smart Job Matching",
      description: "Our AI-powered algorithm matches you with jobs that fit your skills, experience, and preferences.",
    },
    {
      icon: "⚡",
      title: "Instant Applications",
      description: "Apply to multiple jobs with one click using your saved profile and resume.",
    },
    {
      icon: "📊",
      title: "Career Analytics",
      description: "Track your application progress and get insights on market trends and salary data.",
    },
    {
      icon: "🤝",
      title: "Direct Employer Connect",
      description: "Connect directly with hiring managers and get faster responses to your applications.",
    },
    {
      icon: "📚",
      title: "Career Resources",
      description: "Access interview tips, resume templates, and career guidance from industry experts.",
    },
    {
      icon: "🔔",
      title: "Real-time Alerts",
      description: "Get notified instantly when new jobs matching your criteria are posted.",
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-cyan-50"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-full opacity-20 blur-3xl animate-float"></div>
        <div className="absolute bottom-10 left-10 w-64 h-64 bg-gradient-to-tr from-cyan-200 to-blue-200 rounded-full opacity-20 blur-2xl animate-float-delayed"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Find Your{" "}
                <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Dream Job
                </span>{" "}
                Today
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Connect with top employers, discover exciting opportunities, and take the next step in your career
                journey with our AI-powered job matching platform.
              </p>

              {/* Search Bar */}
              <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-100">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <svg
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <input
                        type="text"
                        value={jobText}
                        disabled
                        placeholder="Job title, keywords, or company"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="relative">
                      <svg
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <input
                        type="text"
                        value={locationText}
                        disabled
                        placeholder="City, state, or remote"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                  <Link to={'/jobs'}>
                  <button className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                    Search Jobs
                  </button>
                  </Link>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to={'/jobs'}>
                <button className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                  Get Started Free
                </button>
                </Link>
                <Link to={'/admin/create-job'}>
                <button className="px-8 py-4 border-2 border-indigo-600 text-indigo-600 font-semibold rounded-xl hover:bg-indigo-50 transition-all duration-200">
                  For Employers
                </button>
                </Link>
              </div>
            </div>

            {/* Right Content - Hero Illustration */}
            <div className="relative">
              <div className="bg-gradient-to-br from-indigo-100 to-purple-100 rounded-3xl p-8 shadow-2xl">
                <div className="grid grid-cols-2 gap-4">
                  {/* Job Cards */}
                  <div className="bg-white rounded-2xl p-4 shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-300">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">TC</span>
                      </div>
                      <div className="ml-3">
                        <h4 className="font-semibold text-sm">TCS</h4>
                        <p className="text-xs text-gray-500">Software Engineer</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600">
                      <p>💰 ₹23k - ₹45k</p>
                      <p>📍 Bangalore, India</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-4 shadow-lg transform -rotate-3 hover:rotate-0 transition-transform duration-300 mt-8">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">HC</span>
                      </div>
                      <div className="ml-3">
                        <h4 className="font-semibold text-sm">HealthCare+</h4>
                        <p className="text-xs text-gray-500">Nurse Practitioner</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600">
                      <p>💰 ₹85k - ₹105k</p>
                      <p>📍 Thane, Maharashtra</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-4 shadow-lg transform rotate-2 hover:rotate-0 transition-transform duration-300">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">MF</span>
                      </div>
                      <div className="ml-3">
                        <h4 className="font-semibold text-sm">MarketFlow</h4>
                        <p className="text-xs text-gray-500">Marketing Manager</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600">
                      <p>💰 ₹75k - ₹95k</p>
                      <p>📍 Remote</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-4 shadow-lg transform -rotate-2 hover:rotate-0 transition-transform duration-300 mt-4">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">FB</span>
                      </div>
                      <div className="ml-3">
                        <h4 className="font-semibold text-sm">HDB Finance</h4>
                        <p className="text-xs text-gray-500">Financial Analyst</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600">
                      <p>💰 ₹65k - ₹80k</p>
                      <p>📍 Delhi, India</p>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
                  <span className="text-sm">💼</span>
                </div>
                <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-pink-400 rounded-full flex items-center justify-center animate-pulse">
                  <span className="text-xs">✨</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stats.jobs.toLocaleString()}+</div>
              <div className="text-indigo-100 text-lg">Active Jobs</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stats.companies.toLocaleString()}+</div>
              <div className="text-indigo-100 text-lg">Companies</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stats.candidates.toLocaleString()}+</div>
              <div className="text-indigo-100 text-lg">Job Seekers</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stats.placements.toLocaleString()}+</div>
              <div className="text-indigo-100 text-lg">Successful Placements</div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Categories Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Explore Job Categories</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover opportunities across diverse industries and find the perfect role that matches your skills and
              interests.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {jobCategories.map((category, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden group"
              >
                <div className={`h-2 bg-gradient-to-r ₹{category.color}`}></div>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="text-3xl mr-4">{category.icon}</div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{category.domain}</h3>
                      <p className="text-indigo-600 font-semibold">{category.jobs.toLocaleString()}+ jobs catered</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {category.categories.map((cat, idx) => (
                      <div
                        key={idx}
                        className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm mr-2 mb-2 group-hover:bg-indigo-50 group-hover:text-indigo-700 transition-colors duration-200"
                      >
                        {cat}
                      </div>
                    ))}
                  </div>
                  <Link to={'/jobs?domain=' + category.domain.toLowerCase()}>
                  <button className="mt-4 w-full py-2 text-indigo-600 font-semibold hover:text-indigo-700 transition-colors duration-200">
                    View All Jobs →
                  </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Jobs247?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our platform is designed to make your job search efficient, effective, and enjoyable with cutting-edge
              features.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-2xl hover:bg-gray-50 transition-all duration-300 group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Success Stories</h2>
            <p className="text-xl text-gray-600">
              Hear from professionals who found their dream jobs through Jobs247
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <svg key={i} className="w-6 h-6 text-yellow-400 fill-current" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <blockquote className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
                  "{testimonials[currentTestimonial].content}"
                </blockquote>
                <div className="flex items-center justify-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                    {testimonials[currentTestimonial].image}
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-gray-900 text-lg">{testimonials[currentTestimonial].name}</div>
                    <div className="text-gray-600">{testimonials[currentTestimonial].role}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial Navigation */}
            <div className="flex justify-center mt-8 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ₹{
                    index === currentTestimonial ? "bg-indigo-600" : "bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Find Your Dream Job?</h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
            Join thousands of professionals who have successfully found their perfect career match through Jobs247.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={'/jobs'}>
            <button className="px-8 py-4 bg-white text-indigo-600 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
              Start Job Search
            </button>
            </Link>
            <Link to={'/admin/create-job'}>
            <button className="px-8 py-4 border-2 border-white text-white font-semibold rounded-xl hover:bg-white hover:text-indigo-600 transition-all duration-200">
              Post a Job
            </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-white  rounded-xl flex items-center justify-center shadow-lg mr-3">
                <img src="logo512.png" />
                  
                  {/* <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0H8m8 0v2a2 2 0 002 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8a2 2 0 012-2V8"
                    />
                  </svg> */}
                </div>
                <div>
                  <h3 className="text-xl font-bold">Jobs247</h3>
                  <p className="text-gray-400 text-sm">Find Your Dream Job</p>
                </div>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Connecting talented professionals with amazing opportunities. Your career journey starts here.
              </p>
              <div className="flex space-x-4">
                <a
                  href="/"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-indigo-600 transition-colors duration-200"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                  </svg>
                </a>
                <a
                  href="/"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-indigo-600 transition-colors duration-200"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a
                  href="/"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-indigo-600 transition-colors duration-200"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001.012.001z.017 0z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Job Seekers */}
            <div>
              <h4 className="text-lg font-semibold mb-6">For Job Seekers</h4>
              <ul className="space-y-3">
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Browse Jobs
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Career Advice
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Resume Builder
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Salary Guide
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Interview Tips
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Job Alerts
                  </a>
                </li>
              </ul>
            </div>

            {/* Employers */}
            <div>
              <h4 className="text-lg font-semibold mb-6">For Employers</h4>
              <ul className="space-y-3">
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Post Jobs
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Find Candidates
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Employer Branding
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Recruitment Solutions
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Success Stories
                  </a>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Company</h4>
              <ul className="space-y-3">
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Careers
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Press
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Blog
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Contact
                  </a>
                </li>
                <li>
                  <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Help Center
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-400 text-sm mb-4 md:mb-0">© 2025 Jobs247. All rights reserved.</div>
              <div className="flex space-x-6 text-sm">
                <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                  Privacy Policy
                </a>
                <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                  Terms of Service
                </a>
                <a href="/" className="text-gray-400 hover:text-white transition-colors duration-200">
                  Cookie Policy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
